import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import { fetchSurebets } from '@/api';
import SEOHead from '@/components/common/SEOHead';
import { LoadingPage } from '@/components/common/Loading';
import { formatOdds, formatPercent, formatDate, formatTime, formatMoney } from '@/lib/formatters';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Zap, ChevronRight, Clock, Coins } from 'lucide-react';

export default function SurebetsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sport, setSport] = useState('');
  const [page, setPage] = useState(1);
  const { t } = useTranslation();
  const { currentLang } = useLanguage();

  useEffect(() => {
    setLoading(true);
    fetchSurebets({ sport: sport || undefined, page, limit: 20 })
      .then(res => { setData(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [sport, page]);

  return (
    <div className="container mx-auto px-4 max-w-7xl py-8" data-testid="surebets-page">
      <SEOHead title={t('surebets.title', 'Surebets')} description={t('surebets.subtitle', 'Guaranteed profit arbitrage opportunities')} />

      <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
        <Link to={`/${currentLang}`} className="hover:text-slate-300">{t('nav.home')}</Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-slate-300">{t('surebets.breadcrumb', 'Surebets')}</span>
      </div>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight uppercase text-slate-50 flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
            <span className="text-3xl md:text-4xl">💰</span>
            {t('surebets.title', 'Surebets')}
          </h1>
          <p className="text-sm text-slate-500 mt-1">{t('surebets.subtitle', 'Guaranteed profit arbitrage opportunities')}</p>
        </div>
        <Select value={sport} onValueChange={v => { setSport(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-[140px] bg-slate-900 border-slate-700 text-sm" data-testid="sport-filter-sb">
            <SelectValue placeholder={t('surebets.allSports', 'All Sports')} />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700">
            <SelectItem value="all">{t('surebets.allSports', 'All Sports')}</SelectItem>
            <SelectItem value="football">{t('nav.football', 'Football')}</SelectItem>
            <SelectItem value="basketball">{t('nav.basketball', 'Basketball')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? <LoadingPage /> : (
        <>
          <div className="space-y-3" data-testid="surebets-list">
            {data?.surebets?.map(sb => (
              <Link key={sb.id} to={`/${currentLang}/match/${sb.match_slug}`}
                className="block border border-emerald-500/20 bg-slate-900/50 hover:bg-slate-900 hover:border-emerald-500/40 rounded-xl p-4 transition-all"
                data-testid={`sb-item-${sb.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-xs font-bold">
                      {formatPercent(sb.profit_percent)} {t('surebets.profit', 'PROFIT')}
                    </Badge>
                    {sb.league_logo && <img src={sb.league_logo} alt="" className="w-4 h-4 rounded object-contain" />}
                    <span className="text-xs text-slate-500">{sb.league_name}</span>
                    <span className="text-xs text-slate-600">{sb.market_name}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-slate-500">
                    <Clock className="w-3 h-3" />
                    <span>{formatDate(sb.start_time)} {formatTime(sb.start_time)}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {sb.home_team_logo && <img src={sb.home_team_logo} alt="" className="w-5 h-5 object-contain" />}
                  <p className="text-sm font-medium text-slate-200">{sb.home_team || sb.match_name?.split(' vs ')[0]}</p>
                  <span className="text-xs text-slate-600">vs</span>
                  <p className="text-sm font-medium text-slate-200">{sb.away_team || sb.match_name?.split(' vs ')[1]}</p>
                  {sb.away_team_logo && <img src={sb.away_team_logo} alt="" className="w-5 h-5 object-contain" />}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {sb.selections?.map(s => (
                    <div key={s.selection} className="bg-slate-800/50 rounded-lg p-2.5">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-slate-400 uppercase font-medium">{s.selection}</span>
                        <span className="text-emerald-400 font-bold tabular-nums">{formatOdds(s.odds)}</span>
                      </div>
                      <p className="text-xs text-slate-500">{s.bookmaker_name}</p>
                      <div className="flex items-center justify-between mt-1 text-xs">
                        <span className="text-slate-500">{t('surebets.stake', 'Stake:')} <span className="text-slate-300 tabular-nums">{formatMoney(s.stake, sb.currency)}</span></span>
                        <span className="text-emerald-400 tabular-nums">{formatMoney(s.potential_return, sb.currency)}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-800/50 text-xs">
                  <span className="text-slate-500">{t('surebets.bankroll', 'Bankroll:')} {formatMoney(sb.bankroll, sb.currency)}</span>
                  <span className="text-slate-500">{t('surebets.inverse', 'Inverse:')} {sb.total_inverse}</span>
                </div>
              </Link>
            ))}
            {!data?.surebets?.length && (
              <div className="text-center py-12">
                <Zap className="w-10 h-10 text-slate-700 mx-auto mb-3" />
                <p className="text-slate-400 mb-2">{t('surebets.noResults', 'Şu an aktif kesin bahis fırsatı bulunamadı')}</p>
                <p className="text-xs text-slate-600">Arbitraj fırsatları piyasa hareketlerine göre anlık olarak güncellenir.</p>
              </div>
            )}
          </div>

          {data?.pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              {Array.from({ length: Math.min(data.pages, 5) }, (_, i) => i + 1).map(p => (
                <button key={p} onClick={() => setPage(p)}
                  className={`px-3 py-1.5 rounded text-sm ${page === p ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}>
                  {p}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
